import { PTTEvent } from '../types/ptt-event';
import { PTTStatus } from '../types/ptt-status';

export class SecurePTTService {
  private ws: WebSocket | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private readonly maxReconnectAttempts: number = 5;
  private readonly reconnectDelay: number = 2000; // 2 seconds
  private readonly initialConnectionDelay: number = 300; // 300ms delay for clean transitions
  private shouldReconnect: boolean = true;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private instanceId: string;
  private isInitializing: boolean = false;
  private initializationFailed: boolean = false;

  // Callbacks for PTT events (support multiple subscribers)
  private pttEventCallbacks: ((event: PTTEvent) => void)[] = [];
  private pttStatusCallbacks: ((status: PTTStatus) => void)[] = [];

  // Configuration
  private readonly pttWebSocketUrl: string;

  constructor() {
    this.instanceId = Math.random().toString(36).substring(7);
    this.pttWebSocketUrl = process.env.NEXT_PUBLIC_PTT_WEBSOCKET_URL || 'wss://127.0.0.1:8081/ptt';
    
    // Only log in browser environment
    if (typeof window !== 'undefined') {
      console.log(`🎙️ SecurePTTService instance created: ${this.instanceId}`);
      console.log(`🎙️ PTT WebSocket URL: ${this.pttWebSocketUrl}`);
    }
  }

  async initialize(): Promise<void> {
    // Skip initialization in server-side rendering
    if (typeof window === 'undefined') {
      console.log('🎙️ Skipping PTT service initialization in SSR');
      return;
    }

    if (this.isInitializing) {
      console.log('🎙️ PTT service initialization already in progress');
      return;
    }

    if (this.ws && this.isConnected) {
      if (!this.initializationFailed) {
        console.log('🎙️ PTT service already initialized');
        return;
      }
      console.log('🎙️ Previous initialization failed, cleaning up and retrying...');
      this.cleanup();
    }

    try {
      this.isInitializing = true;
      this.initializationFailed = false;
      
      console.log('🎙️ Starting PTT service initialization...');
      
      // Add initial delay to prevent overlap with closing connections
      console.log(`⏱️ [${this.instanceId}] Waiting ${this.initialConnectionDelay}ms for clean connection transition...`);
      await new Promise(resolve => setTimeout(resolve, this.initialConnectionDelay));
      
      await this.connect();
      console.log('🎙️ PTT service initialization completed');
    } catch (error) {
      console.error('🎙️ Failed to initialize PTT service:', error);
      this.initializationFailed = true;
      this.cleanup();
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  private async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected) {
        console.log('🎙️ PTT WebSocket already connected');
        resolve();
        return;
      }

      console.log(`🎙️ [${this.instanceId}] Attempting to connect to PTT WebSocket: ${this.pttWebSocketUrl}`);
      
      try {
        this.ws = new WebSocket(this.pttWebSocketUrl);
        
        this.ws.onopen = () => {
          console.log('✅ PTT WebSocket connected');
          console.log('🔍 DEBUG: PTT WebSocket connection established at:', new Date().toISOString());
          console.log('🔍 DEBUG: PTT WebSocket URL:', this.ws?.url);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          console.log('📨 Received PTT WebSocket message:', event.data);
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log('❌ PTT WebSocket disconnected', event.code, event.reason);
          this.isConnected = false;
          
          // Only attempt to reconnect if we didn't manually close it
          if (this.shouldReconnect && event.code !== 1000) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('💥 PTT WebSocket error:', error);
          this.isConnected = false;
          reject(error);
        };

        // Set a timeout for the initial connection
        setTimeout(() => {
          if (!this.isConnected) {
            reject(new Error('PTT WebSocket connection timeout'));
          }
        }, 10000); // 10 second timeout

      } catch (error) {
        console.error('🎙️ Error creating PTT WebSocket connection:', error);
        this.isConnected = false;
        reject(error);
      }
    });
  }

  private handleMessage(data: string): void {
    try {
      const parsedData = JSON.parse(data);
      
      // Handle PTT events (actual format from PTT server)
      if (parsedData.type === 'ptt') {
        const pttEvent: PTTEvent = {
          type: parsedData.state === 'press' ? 'press' : parsedData.state === 'release' ? 'release' : 'hold',
          deviceId: parsedData.device_id,
          duration: parsedData.duration,
          timestamp: parsedData.ts || parsedData.timestamp || Date.now(),
          metadata: {
            raw: parsedData.raw,
            originalType: parsedData.type,
            originalState: parsedData.state
          }
        };
        
        console.log('🎙️ Processing PTT event:', pttEvent);
        
        // Notify all registered callbacks
        this.pttEventCallbacks.forEach(callback => {
          try {
            callback(pttEvent);
          } catch (error) {
            console.error('🎙️ Error in PTT event callback:', error);
          }
        });
      }
      
      // Handle raw messages (informational, don't trigger audio gate)
      else if (parsedData.type === 'raw') {
        console.log('🎙️ Received raw PTT data:', parsedData.raw);
        // Raw messages are informational only, don't create PTT events
      }
      
      // Handle acknowledgment messages (informational)
      else if (parsedData.type === 'ack') {
        console.log('🎙️ Received PTT acknowledgment:', parsedData.raw);
        // ACK messages are informational only
      }
      
      // Handle legacy PTT status updates (if any)
      else if (parsedData.type === 'ptt_status') {
        const pttStatus: PTTStatus = {
          isActive: Boolean(parsedData.is_active),
          connectionStatus: parsedData.connection_status || 'connected',
          audioLevel: parsedData.audio_level,
          device: parsedData.device,
          timestamp: parsedData.ts || parsedData.timestamp || Date.now()
        };
        
        // Notify all registered status callbacks
        this.pttStatusCallbacks.forEach(callback => {
          try {
            callback(pttStatus);
          } catch (error) {
            console.error('🎙️ Error in PTT status callback:', error);
          }
        });
      }
      
      else {
        console.log('🎙️ Received unknown PTT message type:', parsedData.type, parsedData);
      }
      
    } catch (error) {
      console.error('🎙️ Error parsing PTT message:', error, data);
    }
  }

  private attemptReconnect(): void {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`❌ [${this.instanceId}] PTT max reconnection attempts reached. Giving up.`);
      this.shouldReconnect = false;
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts; // Exponential backoff
    
    console.log(`🔄 [${this.instanceId}] Attempting to reconnect PTT... (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
    
    this.reconnectTimeoutId = setTimeout(() => {
      if (this.shouldReconnect && !this.isConnected) {
        this.connect().catch(error => {
          console.error('🎙️ PTT reconnection failed:', error);
        });
      }
    }, delay);
  }

  // Manual reconnect method
  async reconnect(): Promise<void> {
    console.log('🔄 PTT manual reconnect requested');
    this.cleanup();
    this.reconnectAttempts = 0;
    this.shouldReconnect = true;
    
    // Add delay for clean transition
    console.log(`⏱️ [${this.instanceId}] Waiting ${this.initialConnectionDelay}ms for clean reconnection transition...`);
    await new Promise(resolve => setTimeout(resolve, this.initialConnectionDelay));
    
    this.connect().catch(error => {
      console.error('🎙️ PTT manual reconnection failed:', error);
    });
  }

  // Check if PTT service is valid/connected
  isPTTServiceValid(): boolean {
    return this.isConnected;
  }

  // Get connection status
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  // Get detailed connection info
  getConnectionInfo(): { 
    isConnected: boolean; 
    reconnectAttempts: number; 
    maxReconnectAttempts: number;
    url: string;
  } {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      url: this.pttWebSocketUrl
    };
  }

  // Register callback for PTT events
  onPTTEvent(callback: (event: PTTEvent) => void): void {
    console.log('🎙️ Registering PTT event callback');
    if (!this.pttEventCallbacks.includes(callback)) {
      this.pttEventCallbacks.push(callback);
    }
  }

  // Register callback for PTT status updates
  onPTTStatus(callback: (status: PTTStatus) => void): void {
    console.log('🎙️ Registering PTT status callback');
    if (!this.pttStatusCallbacks.includes(callback)) {
      this.pttStatusCallbacks.push(callback);
    }
  }

  // Unregister PTT event callback
  offPTTEvent(callback: (event: PTTEvent) => void): void {
    const index = this.pttEventCallbacks.indexOf(callback);
    if (index > -1) {
      this.pttEventCallbacks.splice(index, 1);
      console.log('🎙️ Unregistered PTT event callback');
    }
  }

  // Unregister PTT status callback
  offPTTStatus(callback: (status: PTTStatus) => void): void {
    const index = this.pttStatusCallbacks.indexOf(callback);
    if (index > -1) {
      this.pttStatusCallbacks.splice(index, 1);
      console.log('🎙️ Unregistered PTT status callback');
    }
  }

  // Send data through PTT WebSocket (if needed for bidirectional communication)
  send(data: any): boolean {
    if (!this.isConnected || !this.ws) {
      console.error('❌ PTT WebSocket not connected. Cannot send data.');
      return false;
    }

    try {
      const jsonData = JSON.stringify(data);
      console.log('📤 Sending PTT WebSocket data:', jsonData);
      this.ws.send(jsonData);
      return true;
    } catch (error) {
      console.error('❌ Error sending PTT WebSocket data:', error);
      return false;
    }
  }

  // Cleanup method
  cleanup(): void {
    console.log('🎙️ Cleaning up PTT service...');
    
    this.shouldReconnect = false;
    this.isConnected = false;
    
    // Clear any pending reconnect timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    
    // Close WebSocket connection
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    
    // Reset state
    this.reconnectAttempts = 0;
    this.isInitializing = false;
    this.initializationFailed = false;
    
    // Clear all callbacks
    this.pttEventCallbacks.length = 0;
    this.pttStatusCallbacks.length = 0;
    
    console.log('🎙️ PTT service cleanup completed');
  }
}

// Global singleton instance with hot reload protection
declare global {
  var __securePTTServiceInstance: SecurePTTService | undefined;
}

// Create singleton instance
export const securePTTService = (() => {
  // Use global variable to persist across hot reloads
  if (!global.__securePTTServiceInstance) {
    if (typeof window !== 'undefined') {
      console.log('🎙️ Creating NEW SecurePTTService singleton instance');
    }
    global.__securePTTServiceInstance = new SecurePTTService();
  } else {
    if (typeof window !== 'undefined') {
      console.log('🎙️ Reusing EXISTING SecurePTTService singleton instance');
    }
  }
  return global.__securePTTServiceInstance;
})();

// Clean up on hot reload (development only)
if (typeof window !== 'undefined' && (window as any).securePTTServiceCleanup) {
  console.log('🎙️ Cleaning up previous SecurePTTService instance');
  (window as any).securePTTServiceCleanup();
}

if (typeof window !== 'undefined') {
  (window as any).securePTTServiceCleanup = () => {
    if (global.__securePTTServiceInstance) {
      global.__securePTTServiceInstance.cleanup();
      global.__securePTTServiceInstance = undefined;
    }
  };
}
